<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Workshop Jobs</h1>
            <p class="text-orange-100 text-lg">Manage and track workshop assignments</p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="refreshJobs"
              :disabled="loading"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
      <div 
        @click="filterByStatus('pending')"
        :class="[
          'bg-white overflow-hidden shadow-lg rounded-xl border cursor-pointer hover:shadow-xl transition-shadow duration-200',
          currentFilter === 'pending' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'
        ]"
      >
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Pending Jobs</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.pending || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div 
        @click="filterByStatus('in_workshop')"
        :class="[
          'bg-white overflow-hidden shadow-lg rounded-xl border cursor-pointer hover:shadow-xl transition-shadow duration-200',
          currentFilter === 'in_workshop' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
        ]"
      >
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">In Progress</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.in_workshop || stats.in_progress || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div 
        @click="filterByStatus('completed')"
        :class="[
          'bg-white overflow-hidden shadow-lg rounded-xl border cursor-pointer hover:shadow-xl transition-shadow duration-200',
          currentFilter === 'completed' ? 'border-green-500 bg-green-50' : 'border-gray-200'
        ]"
      >
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Completed</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.completed || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div 
        @click="filterByStatus('delivered')"
        :class="[
          'bg-white overflow-hidden shadow-lg rounded-xl border cursor-pointer hover:shadow-xl transition-shadow duration-200',
          currentFilter === 'delivered' ? 'border-teal-500 bg-teal-50' : 'border-gray-200'
        ]"
      >
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Delivered</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.delivered || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <div 
        @click="filterByStatus('all')"
        :class="[
          'bg-white overflow-hidden shadow-lg rounded-xl border cursor-pointer hover:shadow-xl transition-shadow duration-200',
          currentFilter === 'all' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
        ]"
      >
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Total Jobs</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.total || 0 }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>



    <!-- Jobs Table -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
      <div class="px-4 py-5 sm:p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading jobs...</p>
        </div>
        
        <div v-else-if="jobs.length === 0" class="text-center py-8">
          <div class="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
          <p class="text-gray-500">Try adjusting your filters or check back later.</p>
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="job in jobs"
            :key="job.id"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex justify-between items-start mb-3">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ job.job_id || `Job #${job.id}` }}
                </h3>
                <p class="text-sm text-gray-600">
                  Order #{{ job.order_no }} • {{ job.customer_name }}
                </p>
              </div>
              <div class="text-right">
                <span
                  :class="getStatusClass(job.status)"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ formatStatus(job.status) }}
                </span>
                <p class="text-sm text-gray-500 mt-1">
                  Due: {{ formatDate(job.due_date) }}
                </p>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Workshop User:</span> {{ job.workshop_user_name || 'Not assigned' }}
                </p>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Products:</span> {{ job.product_count || 0 }} {{ job.product_count === 1 ? 'product' : 'products' }}, {{ job.item_count || 0 }} {{ job.item_count === 1 ? 'item' : 'items' }}
                </p>
              </div>
              <div>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Created:</span> {{ formatDate(job.created_at) }}
                </p>
                <p v-if="job.completed_at" class="text-sm text-gray-600">
                  <span class="font-medium">Completed:</span> {{ formatDate(job.completed_at) }}
                </p>
              </div>
            </div>
            
            <div class="flex space-x-2">
              <button
                @click="viewJobDetails(job.id)"
                class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
              >
                View Details
              </button>
              <button
                v-if="job.status === 'pending'"
                @click="updateJobStatus(job.id, 'in_workshop')"
                class="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors duration-200"
              >
                Start Work
              </button>
              <button
                v-if="job.status === 'in_workshop'"
                @click="updateJobStatus(job.id, 'completed')"
                class="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors duration-200"
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
        
        <!-- Pagination -->
        <div v-if="pagination.totalPages > 1" class="mt-6 flex justify-center">
          <nav class="flex space-x-2">
            <button
              @click="changePage(pagination.page - 1)"
              :disabled="pagination.page <= 1"
              class="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
            >
              Previous
            </button>
            <span class="px-3 py-2 text-sm text-gray-700">
              Page {{ pagination.page }} of {{ pagination.totalPages }}
            </span>
            <button
              @click="changePage(pagination.page + 1)"
              :disabled="pagination.page >= pagination.totalPages"
              class="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <!-- Job Details Modal -->
  <div v-if="selectedJob" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div class="p-6">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-semibold text-gray-900">Job Details {{ selectedJob.job_id || `#${selectedJob.id}` }}</h3>
          <button
            @click="selectedJob = null"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="space-y-6">
          <!-- Job Information -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-2">Order Information</h4>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Order Number:</span>
                    <span class="text-sm font-medium">#{{ selectedJob.order_no }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Customer:</span>
                    <span class="text-sm font-medium">{{ selectedJob.customer_name }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Phone:</span>
                    <span class="text-sm font-medium">{{ selectedJob.customer_phone }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Email:</span>
                    <span class="text-sm font-medium">{{ selectedJob.customer_email }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="space-y-4">
              <div>
                <h4 class="font-medium text-gray-900 mb-2">Job Information</h4>
                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Status:</span>
                    <span
                      :class="getStatusClass(selectedJob.status)"
                      class="px-2 py-1 text-xs font-medium rounded-full"
                    >
                      {{ formatStatus(selectedJob.status) }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Assigned To:</span>
                    <span class="text-sm font-medium">{{ selectedJob.workshop_user_name || 'Unassigned' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Due Date:</span>
                    <span class="text-sm font-medium">{{ formatDate(selectedJob.due_date) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Created:</span>
                    <span class="text-sm font-medium">{{ formatDate(selectedJob.created_at) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-500">Estimated Amount:</span>
                    <span class="text-sm font-medium">₹{{ selectedJob.total_est?.toLocaleString() || '0' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Combined Totals Section -->
          <div v-if="selectedJob.items && selectedJob.items.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 class="font-semibold text-blue-900 mb-3">Order Summary</h4>
            <div class="grid grid-cols-3 gap-4 text-center">
              <div>
                <p class="text-sm text-blue-600">Total Quantity</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalQuantity(selectedJob.items) }}</p>
              </div>
              <div>
                <p class="text-sm text-blue-600">Total Gross Weight</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalGrossWeight(selectedJob.items) }}g</p>
              </div>
              <div>
                <p class="text-sm text-blue-600">Total Net Weight</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalNetWeight(selectedJob.items) }}g</p>
              </div>
            </div>
          </div>

          <!-- Items -->
          <div v-if="selectedJob.items && selectedJob.items.length > 0">
            <h4 class="font-medium text-gray-900 mb-3">Items</h4>
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="space-y-3">
                <div
                  v-for="item in selectedJob.items"
                  :key="item.id"
                  class="bg-white rounded p-3 border border-gray-200"
                >
                  <div class="flex gap-4">
                    <!-- Product Images -->
                    <div v-if="item.images && item.images.length > 0" class="flex-shrink-0">
                      <div class="relative">
                        <img
                          :src="getImageUrl(item.images[0])"
                          :alt="item.name"
                          class="w-20 h-20 object-contain rounded-lg border border-gray-200"
                          @error="handleImageError"
                        />
                        <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          +{{ item.images.length - 1 }}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Product Details -->
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <h5 class="font-medium text-gray-900">{{ item.name }}</h5>
                          <p class="text-sm text-gray-600">{{ item.sku }}</p>
                          <p class="text-sm text-gray-600">{{ item.category }} • {{ item.metal }}</p>
                          <div class="mt-2 space-y-1">
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Gross Weight:</span> {{ item.gross_weight || 0 }}g
                            </p>
                            <p class="text-sm text-gray-600">
                              <span class="font-medium">Net Weight:</span> {{ item.net_weight || 0 }}g
                            </p>
                          </div>
                        </div>
                        <div class="text-right">
                          <p class="text-sm font-medium text-gray-900">Qty: {{ item.quantity || item.qty }}</p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Gross Total:</span> {{ getItemGrossTotal(item) }}g
                          </p>
                          <p class="text-sm text-gray-600">
                            <span class="font-medium">Net Total:</span> {{ getItemNetTotal(item) }}g
                          </p>
                        </div>
                      </div>
                      
                      <!-- Additional Images Gallery -->
                      <div v-if="item.images && item.images.length > 1" class="mt-2">
                        <div class="flex gap-2 overflow-x-auto">
                          <img
                            v-for="(image, index) in item.images.slice(1, 4)"
                            :key="index"
                            :src="getImageUrl(image)"
                            :alt="`${item.name} - Image ${index + 2}`"
                            class="w-12 h-12 object-contain rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                            @click="viewImageModal(item.images, index + 1, item.name)"
                            @error="handleImageError"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Notes -->
          <div v-if="selectedJob.notes">
            <h4 class="font-medium text-gray-900 mb-2">Notes</h4>
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-gray-700">{{ selectedJob.notes }}</p>
            </div>
          </div>
          
          <!-- Status History -->
          <div v-if="selectedJob.history && selectedJob.history.length > 0">
            <h4 class="font-medium text-gray-900 mb-3">Status History</h4>
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="space-y-2">
                <div
                  v-for="entry in selectedJob.history"
                  :key="entry.id"
                  class="flex justify-between items-center text-sm"
                >
                  <div>
                    <span class="font-medium">{{ formatStatus(entry.status) }}</span>
                    <span class="text-gray-500 ml-2">by {{ entry.updated_by_name }}</span>
                  </div>
                  <span class="text-gray-500">{{ formatDate(entry.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Image Modal -->
  <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black bg-opacity-75" @click="closeImageModal"></div>
    <div class="relative bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">{{ currentProductName || (currentImages.length > 0 ? 'Product Images' : 'No Images Available') }}</h3>
        <button @click="closeImageModal" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- Modal Content -->
      <div class="p-4">
        <div class="relative">
          <!-- Main Image -->
          <div class="flex justify-center items-center bg-gray-100 rounded-lg overflow-hidden" style="min-height: 400px;">
            <img
              v-if="currentImages.length > 0"
              :src="getImageUrl(currentImages[currentImageIndex])"
              :alt="`Product Image ${currentImageIndex + 1}`"
              class="max-w-full max-h-96 object-contain"
              @error="handleImageError"
            />
            <div v-else class="text-gray-500 text-center">
              <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>No images available</p>
            </div>
          </div>
          
          <!-- Navigation Buttons -->
          <button
            v-if="currentImages.length > 1"
            @click="previousImage"
            class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
          >
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            v-if="currentImages.length > 1"
            @click="nextImage"
            class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
          >
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <!-- Thumbnail Navigation -->
        <div v-if="currentImages.length > 1" class="mt-4">
          <div class="flex justify-center gap-2 overflow-x-auto">
            <button
              v-for="(image, index) in currentImages"
              :key="index"
              @click="setCurrentImage(index)"
              :class="[
                'flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all duration-200',
                currentImageIndex === index
                  ? 'border-orange-500 shadow-lg'
                  : 'border-gray-300 hover:border-gray-400'
              ]"
            >
              <img
                :src="getImageUrl(image)"
                :alt="`Thumbnail ${index + 1}`"
                class="w-full h-full object-cover"
                @error="handleImageError"
              />
            </button>
          </div>
        </div>
        
        <!-- Image Counter -->
        <div v-if="currentImages.length > 1" class="mt-2 text-center text-sm text-gray-600">
          {{ currentImageIndex + 1 }} of {{ currentImages.length }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { workshopAPI, adminAPI } from '@/services/api'
import { PAGINATION_LIMITS, createPagination } from '@/constants/pagination'

export default {
  name: 'WorkshopJobs',
  setup() {
    const jobs = ref([])
    const loading = ref(false)
    const workshopUsers = ref([])
    const selectedJob = ref(null)
    const stats = ref({
      pending: 0,
      in_progress: 0,
      completed: 0,
      total: 0
    })
    const currentFilter = ref('all')
    const pagination = ref(createPagination(PAGINATION_LIMITS.WORKSHOP_JOBS))
    
    // Image modal variables
    const showImageModal = ref(false)
    const currentImages = ref([])
    const currentImageIndex = ref(0)
    const currentProductName = ref('')
    

    
    const loadJobs = async () => {
      try {
        loading.value = true
        
        const params = {
          page: pagination.value.page,
          limit: pagination.value.limit,
          status: currentFilter.value === 'all' ? '' : currentFilter.value
        }
        
        console.log('Loading jobs with params:', params)
        const response = await workshopAPI.getJobs(params)
        console.log('Jobs response:', response.data)
        jobs.value = response.data.jobs
        pagination.value = response.data.pagination
        stats.value = response.data.stats || {}
        console.log('Stats loaded:', stats.value)
      } catch (error) {
        console.error('Failed to load jobs:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadWorkshopUsers = async () => {
      try {
        const response = await adminAPI.getUsers({ role: 'workshop', active: true })
        workshopUsers.value = response.data.users
      } catch (error) {
        console.error('Failed to load workshop users:', error)
      }
    }
    
    const loadStats = async () => {
      try {
        console.log('Loading workshop stats...')
        const response = await workshopAPI.getStats()
        console.log('Stats response:', response.data)
        stats.value = response.data.stats || response.data || {}
        console.log('Stats set to:', stats.value)
      } catch (error) {
        console.error('Failed to load stats:', error)
      }
    }
    
    const filterByStatus = (status) => {
      currentFilter.value = status
      pagination.value.page = 1
      loadJobs()
    }
    
    const refreshJobs = () => {
      pagination.value.page = 1
      loadJobs()
    }
    
    const changePage = (page) => {
      pagination.value.page = page
      loadJobs()
    }
    
    const updateJobStatus = async (jobId, newStatus) => {
      try {
        await workshopAPI.updateJobStatus(jobId, newStatus)
        await loadJobs() // Refresh the list
      } catch (error) {
        console.error('Failed to update job status:', error)
      }
    }
    
    const viewJobDetails = async (jobId) => {
      try {

        // Find the job in the existing jobs list first
        const existingJob = jobs.value.find(job => job.id === jobId)
        if (existingJob) {
          console.log('Found job in existing list:', existingJob)
          selectedJob.value = existingJob
          return
        }
        
        // If not found in list, fetch from API
        const response = await workshopAPI.getJob(jobId)
        console.log('Job details response:', response.data)
        selectedJob.value = response.data.job
        console.log('Selected job set to:', selectedJob.value)
      } catch (error) {
        console.error('Failed to load job details:', error)
      }
    }
    
    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        in_workshop: 'bg-blue-100 text-blue-800',
        in_progress: 'bg-blue-100 text-blue-800', // Keep for backward compatibility
        completed: 'bg-green-100 text-green-800',
        delivered: 'bg-teal-100 text-teal-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }
    
    const formatStatus = (status) => {
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }
    
    // Image handling functions
    const getImageUrl = (imageName) => {
      if (!imageName) return ''
      
      // If it's already a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const handleImageError = (event) => {
      // Set a fallback image when the actual image fails to load
      event.target.src = 'data:image/svg+xml;base64,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'
    }

    // Image modal functions
    const viewImageModal = (images, startIndex = 0, productName = '') => {
      currentImages.value = images
      currentImageIndex.value = startIndex
      currentProductName.value = productName
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      currentImages.value = []
      currentImageIndex.value = 0
      currentProductName.value = ''
    }

    const nextImage = () => {
      if (currentImageIndex.value < currentImages.value.length - 1) {
        currentImageIndex.value++
      } else {
        currentImageIndex.value = 0
      }
    }

    const previousImage = () => {
      if (currentImageIndex.value > 0) {
        currentImageIndex.value--
      } else {
        currentImageIndex.value = currentImages.value.length - 1
      }
    }

    const setCurrentImage = (index) => {
      currentImageIndex.value = index
    }

    // Helper methods for weight calculations
    const getTotalQuantity = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => total + (item.quantity || item.qty || 0), 0)
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }

    const getItemGrossTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const grossWeight = parseFloat(item.gross_weight) || 0
      return (grossWeight * quantity).toFixed(2)
    }

    const getItemNetTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const netWeight = parseFloat(item.net_weight) || 0
      return (netWeight * quantity).toFixed(2)
    }

    onMounted(() => {
      loadJobs()
      loadWorkshopUsers()
      loadStats()
    })
    
    return {
      jobs,
      loading,
      workshopUsers,
      selectedJob,
      stats,
      currentFilter,
      pagination,

      loadJobs,
      loadWorkshopUsers,
      loadStats,
      filterByStatus,
      refreshJobs,
      changePage,
      updateJobStatus,
      viewJobDetails,
      getStatusClass,
      formatStatus,
      formatDate,

      // Image modal variables and functions
      showImageModal,
      currentImages,
      currentImageIndex,
      currentProductName,
      getImageUrl,
      handleImageError,
      viewImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      setCurrentImage,

      // Weight calculation methods
      getTotalQuantity,
      getTotalGrossWeight,
      getTotalNetWeight,
      getItemGrossTotal,
      getItemNetTotal
    }
  }
}
</script> 